import React, { useState } from 'react';
import { Outlet, useLocation, Navigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { MdWarning, MdLock, MdRefresh } from 'react-icons/md';
import Sidebar from './Sidebar';
import Header  from './Header';
import { RenewalModal } from './NotificationBell';
import { selectSubscriptionWarning, selectDaysUntilExpiry, selectSubscriptionActive, selectIsFeatureEnabled, selectNavigationLabels, logout } from '../../store/authSlice';
import { selectSidebar, setSidebarOpen } from '../../store/uiSlice';

const PAGE_META = {
  '/dashboard':                        { title: 'Dashboard' },
  '/users':                            { title: 'User Management' },

  // Healthcare
  '/healthcare/patients':              { title: 'Patients',           module: 'HEALTHCARE' },
  '/healthcare/appointments':          { title: 'Appointments',       module: 'HEALTHCARE' },
  '/healthcare/doctors':               { title: 'Doctors',            module: 'HEALTHCARE' },
  '/healthcare/invoices':              { title: 'HC Invoices',        module: 'HEALTHCARE' },
  '/healthcare/reports':               { title: 'HC Reports',         module: 'HEALTHCARE' },

  // Factory
  '/factory/products':                 { title: 'Products',           module: 'FACTORY' },
  '/factory/variant-catalog':          { title: 'Variant Catalog',    module: 'FACTORY' },
  '/factory/variant-orders':           { title: 'Variant Orders',     module: 'FACTORY' },
  '/factory/orders':                   { title: 'Production Orders',  module: 'FACTORY' },
  '/factory/customers':                { title: 'Trade Partners',     module: 'FACTORY' },
  '/factory/deliveries':               { title: 'Deliveries',         module: 'FACTORY' },
  '/factory/partner-catalog':          { title: 'Shop',               module: 'FACTORY' },
  '/factory/partner-cart':             { title: 'My Cart',            module: 'FACTORY' },
  '/factory/partner-my-orders':        { title: 'My Orders',          module: 'FACTORY' },
  '/factory/partner-order-requests':   { title: 'Partner Order Requests', module: 'FACTORY' },
  '/factory/reports':                  { title: 'Factory Reports',    module: 'FACTORY' },

  // Billing
  '/billing/invoices':                 { title: 'Invoices',           module: 'BILLING' },
  '/billing/summary':                  { title: 'Billing Summary',    module: 'BILLING' },

  // Expenses
  '/expenses':                         { title: 'Expenses' },

  // Reports
  '/reports':                          { title: 'Reports & Analytics' },
  '/reports/profit-loss':              { title: 'Profit & Loss' },

  // Pharmacy
  '/pharmacy/medicines':               { title: 'Medicines',          module: 'PHARMACY' },
  '/pharmacy/racks':                   { title: 'Racks',              module: 'PHARMACY' },
  '/pharmacy/invoices':                { title: 'Rx Invoices',        module: 'PHARMACY' },
  '/pharmacy/reports':                 { title: 'Rx Reports',         module: 'PHARMACY' },
  '/pharmacy/suppliers':               { title: 'Suppliers',          module: 'PHARMACY' },
  '/pharmacy/purchase-orders':         { title: 'Purchase / GRN',     module: 'PHARMACY' },
  '/pharmacy/customers':               { title: 'Customers',          module: 'PHARMACY' },
  '/pharmacy/returns':                 { title: 'Returns',            module: 'PHARMACY' },
  '/pharmacy/medicine-orders':         { title: 'Medicine Orders',    module: 'PHARMACY' },
  '/pharmacy/settings':                { title: 'Payment Settings',   module: 'PHARMACY' },

  // Settings
  '/settings/payment':                 { title: 'Settings' },
  '/settings/chart-of-accounts':       { title: 'Chart of Accounts' },
  '/settings/master-types':            { title: 'Master Types' },
  '/settings/terminology':             { title: 'Terminology' },
  '/settings/referral':                { title: 'Refer & Earn' },
  '/super-admin/testimonials':         { title: 'Testimonials' },

  // Workforce
  '/workforce/workers':                { title: 'Workers' },
  '/workforce/attendance':             { title: 'Attendance' },
  '/workforce/production':             { title: 'Production Log' },
  '/workforce/payments':               { title: 'Worker Payments' },
  '/workforce/vendors':                { title: 'Vendors & Suppliers' },
  '/workforce/resources':              { title: 'Resource Inventory' },
  '/workforce/equipment':              { title: 'Equipment Tracker' },
  '/workforce/reports':                { title: 'Workforce Reports' },
  '/workforce/sites':                  { title: 'Site Management' },

  // Super Admin
  '/super-admin/tenants':              { title: 'Workspaces' },
  '/super-admin/users':                { title: 'All Users' },
  '/super-admin/subscriptions':        { title: 'Subscription Report' },
  '/super-admin/plan-pricing':         { title: 'Plan Pricing' },
  '/super-admin/contact-messages':     { title: 'Contact Messages' },
  '/super-admin/mfa-setup':            { title: 'MFA Security' },
  '/mfa-setup':                        { title: 'MFA Security' },
  '/super-admin/audit-log':            { title: 'Audit Log' },
};

const PAGE_NAV_LABEL_KEYS = {
  '/factory/products': 'factory.products',
  '/factory/variant-catalog': 'factory.variant-catalog',
  '/factory/variant-orders': 'factory.variant-orders',
  '/factory/orders': 'factory.orders',
  '/factory/customers': 'factory.customers',
  '/factory/deliveries': 'factory.deliveries',
  '/factory/partner-catalog': 'factory.partner-catalog',
  '/factory/partner-order-requests': 'factory.partner-order-requests',
  '/factory/reports': 'factory.reports',
  '/billing/invoices': 'billing.invoices',
  '/workforce/vendors': 'workforce.vendors',
  '/workforce/resources': 'workforce.resources',
  '/settings/referral': 'settings.referral',
};

const routeToNavKey = (path) => String(path || '').replace(/^\//, '').replace(/\//g, '.');
const stripFactoryWord = (value) => String(value || '')
  .replace(/\bfactory\b/ig, ' ')
  .replace(/\s{2,}/g, ' ')
  .trim();

export default function Layout() {
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const resourceSellMappingEnabled = useSelector(selectIsFeatureEnabled('RESOURCE_SELL_MAPPING'));
  const navigationLabels = useSelector(selectNavigationLabels);
  const baseMeta            = PAGE_META[pathname] || {};
  const salesViewMetaByPath = {
    '/factory/products': { ...baseMeta, title: 'Product', module: undefined },
    '/factory/variant-catalog': { ...baseMeta, module: undefined },
    '/factory/variant-orders': { ...baseMeta, module: undefined },
    '/factory/orders': { ...baseMeta, module: undefined },
    '/factory/customers': { ...baseMeta, module: undefined },
    '/factory/deliveries': { ...baseMeta, module: undefined },
    '/factory/partner-catalog': { ...baseMeta, module: undefined },
    '/factory/partner-cart': { ...baseMeta, module: undefined },
    '/factory/partner-my-orders': { ...baseMeta, module: undefined },
    '/factory/partner-order-requests': { ...baseMeta, module: undefined },
    '/factory/reports': { ...baseMeta, title: 'Sales Reports', module: undefined },
  };
  const meta = resourceSellMappingEnabled && salesViewMetaByPath[pathname]
    ? salesViewMetaByPath[pathname]
    : baseMeta;
  const rawDisplayLabel = navigationLabels[PAGE_NAV_LABEL_KEYS[pathname] || routeToNavKey(pathname)];
  const displayLabel = resourceSellMappingEnabled ? stripFactoryWord(rawDisplayLabel) : rawDisplayLabel;
  const displayMeta = displayLabel
    ? { ...meta, title: displayLabel }
    : meta;
  const warning            = useSelector(selectSubscriptionWarning);
  const days               = useSelector(selectDaysUntilExpiry);
  const subscriptionActive = useSelector(selectSubscriptionActive);
  const sidebarOpen        = useSelector(selectSidebar);
  const [showRenew, setShowRenew] = useState(false);

  // Expired subscription degraded mode: allow login + dashboard + renewal only.
  if (!subscriptionActive && pathname !== '/dashboard') {
    return <Navigate to="/dashboard" replace />;
  }

  // Colour: red if 0-2 days, yellow if 3-7 days
  const bannerClass = days !== null && days <= 2
    ? 'bg-red-600 text-white'
    : 'bg-amber-400 text-amber-900';

  return (
    <div className="flex h-screen overflow-hidden bg-neutral-50">
      {/* Mobile overlay — tap outside sidebar to close */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-20 bg-black/40 md:hidden"
          onClick={() => dispatch(setSidebarOpen(false))}
        />
      )}
      <Sidebar />
      <div className="flex flex-col flex-1 overflow-hidden">
        {!subscriptionActive && (
          <div className="flex items-center justify-between gap-3 px-4 py-2 text-xs font-medium bg-red-600 text-white">
            <div className="flex items-center gap-2 min-w-0">
              <MdLock size={16} className="flex-shrink-0" />
              <span className="truncate">Subscription expired. Access is limited to Dashboard until renewal is completed.</span>
            </div>
            <button
              onClick={() => setShowRenew(true)}
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded bg-white/15 hover:bg-white/25"
            >
              <MdRefresh size={14} /> Renew
            </button>
          </div>
        )}
        {warning && (
          <div className={`flex items-center gap-2 px-4 py-2 text-xs font-medium ${bannerClass}`}>
            <MdWarning size={16} className="flex-shrink-0" />
            {warning}
          </div>
        )}
        <Header title={displayMeta.title} module={displayMeta.module} />
        <main className="flex-1 overflow-y-auto p-5">
          <Outlet />
        </main>
      </div>

      {showRenew && (
        <RenewalModal
          metadata={null}
          onClose={() => setShowRenew(false)}
        />
      )}
    </div>
  );
}
